<script lang="ts">
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';

    import unescape from 'lodash/unescape';
    import { onDestroy } from 'svelte';

    import { NORMALIZE_CSS } from '../constants';

    export let provider = '';
    export let head = '';
    export let body = '';

    let iframeContainer: HTMLIFrameElement;
    let rsObserver: ResizeObserver | undefined;
    let msObserver: MutationObserver | undefined;
    // Tracks whether the iframe currently holds a cross-origin document (the
    // Google Maps embed). While true, reading `contentWindow.document` throws.
    let iframeIsCrossOrigin = false;
    // The Google Maps `src` currently loaded into the iframe. Lets re-runs tell an
    // unchanged map (skip) from an edited one (reload) without reading the
    // cross-origin document.
    let loadedMapsSrc = '';

    // Extracts the map URL from a Google Maps embed snippet's `<iframe src="...">`.
    const extractMapsSrc = (iframeString: string): string | null => {
        const match = iframeString.match(/<iframe src="?(https[^"]+)"?/);
        return match ? match[1] : null;
    };

    // Prevents the iframe's height from feeding back into the embed's layout
    // (embeds using % / vh would otherwise grow with the iframe on mobile first paint).
    const HOST_RESET_CSS = '<style>html,body{height:auto;min-height:0;margin:0;}</style>';

    const cleanup = () => {
        rsObserver?.disconnect();
        rsObserver = undefined;
        msObserver?.disconnect();
        msObserver = undefined;
    };

    onDestroy(cleanup);

    $: if (iframeContainer && !rsObserver) {
        requestAnimationFrame(() => {
            // Component may have been destroyed between scheduling and firing —
            // Svelte nulls bind:this refs on unmount.
            if (!iframeContainer) return;

            // The google-maps branch navigates the iframe to a cross-origin URL.
            // While the iframe holds that document, reading `contentWindow.document`
            // below throws a SecurityError — regardless of the current provider — so
            // key this guard off the iframe's cross-origin state, not the provider.
            if (iframeIsCrossOrigin) {
                // The map is already loaded. Skip the rebuild only when it is
                // unchanged; if the provider switched away or the Maps src was
                // edited (e.g. in the preview editor), fall through to restore
                // about:blank and re-run setup so the iframe refreshes.
                if (
                    provider === 'google-maps' &&
                    extractMapsSrc(unescape(body)) === loadedMapsSrc
                ) {
                    return;
                }

                // Restore a same-origin about:blank document before any document
                // access, then re-run this setup once it has loaded (the reactive
                // block re-runs when `iframeIsCrossOrigin` flips back to false).
                iframeContainer.addEventListener(
                    'load',
                    () => {
                        iframeIsCrossOrigin = false;
                    },
                    { once: true },
                );
                iframeContainer.src = 'about:blank';
                return;
            }

            const { contentWindow: window } = iframeContainer;

            if (!window) return;

            const headElem = window.document.getElementsByTagName('head')[0];
            const bodyElem = window.document.getElementsByTagName('body')[0];

            const rawHeadCode = unescape(head);
            const rawBodyCode = unescape(body);

            cleanup();

            if (provider !== 'provenexpert') {
                bodyElem.style.display = 'flex';
                bodyElem.style.justifyContent = 'center';
                bodyElem.style.alignItems = 'flex-start';
            }

            const headNode = window.document
                .createRange()
                .createContextualFragment(
                    `${NORMALIZE_CSS}${HOST_RESET_CSS}<style>body > a {display:inline-block;}body > .trustpilot-widget{width:100%;}</style>${rawHeadCode}`,
                );

            headElem.innerHTML = '';
            bodyElem.innerHTML = '';
            headElem.appendChild(headNode);

            if (provider === 'google-maps') {
                // The reactive block keeps re-running for google-maps because it
                // never creates `rsObserver` (the `!rsObserver` guard stays true);
                // the cross-origin guard above ensures we only re-navigate when the
                // map src actually changes.
                const iframeSrc = extractMapsSrc(rawBodyCode);

                if (!iframeSrc) {
                    // Leave `iframeIsCrossOrigin` false so a later valid update can retry.
                    console.warn(`Google Maps Embed Error`, {
                        data: { error: JSON.stringify(body) },
                    });
                    return;
                }

                iframeIsCrossOrigin = true;
                loadedMapsSrc = iframeSrc;

                iframeContainer.width = '100%';
                iframeContainer.style.border = '0';
                iframeContainer.style.aspectRatio = '4/3';
                // Do not lazy-load: iOS Safari leaves lazy iframes blank (white map)
                // until a repaint is forced (e.g. switching tabs). See FUN-3816.
                iframeContainer.loading = 'eager';
                iframeContainer.referrerPolicy = 'no-referrer-when-downgrade';

                // iOS Safari renders cross-origin iframes (Google Maps) on their
                // own compositing layer and can leave that layer unpainted (white)
                // on first load until something forces a repaint — e.g. switching
                // tabs. Promote the layer up front and force a repaint once the map
                // document has loaded. See FUN-3816.
                iframeContainer.style.transform = 'translateZ(0)';
                iframeContainer.addEventListener(
                    'load',
                    () => {
                        if (!iframeContainer) return;
                        iframeContainer.style.visibility = 'hidden';
                        // Synchronous reflow flushes the iframe's compositing layer.
                        void iframeContainer.offsetHeight;
                        iframeContainer.style.visibility = '';
                    },
                    { once: true },
                );

                iframeContainer.src = iframeSrc;
                return;
            }

            // Inject into a controlled root so we observe the correct element
            // even if the embed rewraps its own DOM later.
            const root = window.document.createElement('div');
            root.style.width = '100%';
            root.appendChild(window.document.createRange().createContextualFragment(rawBodyCode));
            bodyElem.appendChild(root);

            const measure = () => {
                if (!iframeContainer) return;
                // Measure the document, not just our wrapper: child margins can collapse
                // through `root` and leave it shorter than the actual rendered document.
                const docEl = window.document.documentElement;
                let height = Math.max(docEl.scrollHeight, bodyElem.scrollHeight);

                if (provider === 'provenexpert') height += 22; // prevents scrollbar in embed
                iframeContainer.style.height = `${Math.ceil(height)}px`;
            };

            rsObserver = new ResizeObserver(measure);
            rsObserver.observe(root);

            msObserver = new MutationObserver(measure);
            msObserver.observe(root, {
                childList: true,
                subtree: true,
                attributes: true,
                characterData: true,
            });

            measure();

            // Late reflow source — fonts loading after initial paint can change height.
            window.document.fonts?.ready?.then(measure);
        });
    }

    const embedBorderRadiusClass = getBorderRadius('embed');
</script>

<div class="fun-flex fun-justify-center">
    <iframe
        bind:this={iframeContainer}
        class={cn('fun-w-full fun-transition-all max-sm:fun-rounded-none', {
            [embedBorderRadiusClass]: provider !== 'provenexpert',
        })}
        title={`${provider}-embed`}
    />
</div>
